// ---------------------------------------------------------------------------
// Campaign spillover — dash API client (feature #77).
//
// Typed wrapper around the `/api/v1/campaign-spillover` routes exposed by
// `apps/api/src/routes/campaign-spillover.ts`. The API supports two modes:
//   * 'allow'  — accept over-cap conversions (spillover).
//   * 'cancel' — reject over-cap conversions.
//
// This module only covers what the dash form needs:
//   * GET    /:campaignId   — read current policy
//   * POST   /              — create new policy
//   * PUT    /:campaignId   — update existing policy
//
// All calls are relative to `API_BASE` which defaults to "/api/v1" and can
// be overridden via the VITE_API_BASE env for local dev.
// ---------------------------------------------------------------------------

export const SPILLOVER_MODES = ["allow", "cancel"] as const;
export type SpilloverMode = (typeof SPILLOVER_MODES)[number];

export const CAP_TYPES = ["clicks", "conversions", "any"] as const;
export type CapType = (typeof CAP_TYPES)[number];

export type SpilloverView = {
	id: string;
	campaign_id: string;
	mode: SpilloverMode;
	cap_type: CapType;
	created_at: number;
	updated_at: number;
};

export type SpilloverSaveInput = {
	campaignId: string;
	mode: SpilloverMode;
	capType?: CapType;
};

function readApiBase(): string {
	const meta = (import.meta as unknown as {
		env?: Record<string, string | undefined>;
	}).env;
	return (meta?.VITE_API_BASE ?? "/api/v1").replace(/\/$/, "");
}

export const API_BASE = readApiBase();

export class SpilloverApiError extends Error {
	status: number;
	code?: string;
	constructor(status: number, message: string, code?: string) {
		super(message);
		this.status = status;
		this.code = code;
	}
}

async function parseJsonOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text.length > 0) {
		try {
			data = JSON.parse(text);
		} catch {
			throw new SpilloverApiError(res.status, `invalid_json:${res.status}`);
		}
	}
	if (!res.ok) {
		const errObj = (data ?? {}) as { error?: string };
		throw new SpilloverApiError(
			res.status,
			errObj.error ?? `http_${res.status}`,
			errObj.error,
		);
	}
	return data as T;
}

// ---------------------------------------------------------------------------
// GET /:campaignId  — returns null on 404 (no policy yet).
// ---------------------------------------------------------------------------
export async function getSpillover(
	campaignId: string,
	opts: { signal?: AbortSignal; fetcher?: typeof fetch } = {},
): Promise<SpilloverView | null> {
	const id = campaignId.trim();
	if (id.length === 0) {
		throw new SpilloverApiError(400, "invalid_campaign_id");
	}
	const doFetch = opts.fetcher ?? fetch;
	const res = await doFetch(
		`${API_BASE}/campaign-spillover/${encodeURIComponent(id)}`,
		{ method: "GET", signal: opts.signal, headers: { accept: "application/json" } },
	);
	if (res.status === 404) return null;
	const body = await parseJsonOrThrow<{ spillover: SpilloverView }>(res);
	return body.spillover;
}

// ---------------------------------------------------------------------------
// POST / — create a new policy.
// ---------------------------------------------------------------------------
export async function createSpillover(
	input: SpilloverSaveInput,
	opts: { signal?: AbortSignal; fetcher?: typeof fetch } = {},
): Promise<SpilloverView> {
	const doFetch = opts.fetcher ?? fetch;
	const res = await doFetch(`${API_BASE}/campaign-spillover`, {
		method: "POST",
		signal: opts.signal,
		headers: {
			"content-type": "application/json",
			accept: "application/json",
		},
		body: JSON.stringify({
			campaignId: input.campaignId.trim(),
			mode: input.mode,
			capType: input.capType ?? "conversions",
		}),
	});
	const body = await parseJsonOrThrow<{ spillover: SpilloverView }>(res);
	return body.spillover;
}

// ---------------------------------------------------------------------------
// PUT /:campaignId — partial update (mode and/or capType).
// ---------------------------------------------------------------------------
export async function updateSpillover(
	input: SpilloverSaveInput,
	opts: { signal?: AbortSignal; fetcher?: typeof fetch } = {},
): Promise<SpilloverView> {
	const id = input.campaignId.trim();
	if (id.length === 0) {
		throw new SpilloverApiError(400, "invalid_campaign_id");
	}
	const doFetch = opts.fetcher ?? fetch;
	const res = await doFetch(
		`${API_BASE}/campaign-spillover/${encodeURIComponent(id)}`,
		{
			method: "PUT",
			signal: opts.signal,
			headers: {
				"content-type": "application/json",
				accept: "application/json",
			},
			body: JSON.stringify({
				mode: input.mode,
				capType: input.capType ?? "conversions",
			}),
		},
	);
	const body = await parseJsonOrThrow<{ spillover: SpilloverView }>(res);
	return body.spillover;
}

// ---------------------------------------------------------------------------
// saveSpillover — try PUT first, fall back to POST when no policy exists.
// ---------------------------------------------------------------------------
export async function saveSpillover(
	input: SpilloverSaveInput,
	opts: { signal?: AbortSignal; fetcher?: typeof fetch } = {},
): Promise<SpilloverView> {
	try {
		return await updateSpillover(input, opts);
	} catch (err) {
		if (err instanceof SpilloverApiError && err.status === 404) {
			return createSpillover(input, opts);
		}
		throw err;
	}
}

export function isSpilloverMode(v: unknown): v is SpilloverMode {
	return typeof v === "string" && (SPILLOVER_MODES as readonly string[]).includes(v);
}
